import React, { useState, useEffect, useRef } from 'react';
import adminSocketService from '../services/socketService';
import { webrtcManager } from '../services/webrtcManager';
import {
  Search, Sliders, Play, Square, MessageSquare, ShieldAlert, Cpu,
  HardDrive, RefreshCw, X, Plus, Terminal, Power, Globe, AppWindow,
  Users, ChevronDown, Check, ArrowRight, Star, AlertTriangle, Monitor,
  FolderPlus, Grid, List, Activity, Settings, User, Bell, LogOut, Info,
  Maximize2, Bot
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { fetchMessages } from '../store/quickMessageSlice';
import RemoteViewer from './RemoteViewer';
import { API_BASE_URL } from '../config';
import './LiveMonitorView.css';

// --- Background Grid & Glowing Proximity Connections ---
export function ParticlesBg() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId = 0;

    let pts = Array.from({ length: 45 }, () => ({
      x: Math.random() * canvas.offsetWidth,
      y: Math.random() * canvas.offsetHeight,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 2 + 1
    }));

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 1. Grid
      ctx.strokeStyle = 'rgba(255, 122, 26, 0.02)';
      ctx.lineWidth = 1;
      const size = 60;
      for (let x = 0; x < canvas.width; x += size) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += size) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
      }

      // 2. Nodes & Proximity lines
      ctx.fillStyle = 'rgba(255, 122, 26, 0.15)';
      pts.forEach((p, idx) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();

        for (let j = idx + 1; j < pts.length; j++) {
          const p2 = pts[j];
          const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
          if (dist < 110) {
            ctx.strokeStyle = `rgba(255, 122, 26, ${(1 - dist / 110) * 0.08})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      });

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      ro.disconnect();
      cancelAnimationFrame(animId);
    };
  }, []);

  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }} />;
}

// --- High-fidelity Screen Mockups inside the cards ---
function MockScreen({ type, title }) {
  if (type === 'code') {
    return (
      <div style={{ background: '#0D0E11', height: '100%', width: '100%', padding: '8px 12px', fontSize: 7, fontFamily: 'monospace', display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 4, color: '#6A737D' }}>
          <span style={{ color: '#E1E4E8' }}>App.tsx</span>
          <span>index.css</span>
          <span>package.json</span>
        </div>
        <div style={{ display: 'flex', gap: 8, color: '#F9826C' }}>
          <div style={{ color: '#6A737D', textAlign: 'right', userSelect: 'none' }}>
            1<br />2<br />3<br />4<br />5
          </div>
          <div style={{ color: '#E1E4E8' }}>
            <span style={{ color: '#F9826C' }}>import</span> React <span style={{ color: '#F9826C' }}>from</span> <span style={{ color: '#9ECBFF' }}>'react'</span><br />
            <span style={{ color: '#F9826C' }}>function</span> <span style={{ color: '#B392F0' }}>App</span>() &#123;<br />
            &nbsp;&nbsp;<span style={{ color: '#F9826C' }}>return</span> (<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&lt;<span style={{ color: '#85E89D' }}>div</span> className=<span style={{ color: '#9ECBFF' }}>"container"</span>&gt;<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span style={{ color: '#85E89D' }}>h1</span>&gt;Live System&lt;/<span style={{ color: '#85E89D' }}>h1</span>&gt;
          </div>
        </div>
        <div style={{ marginTop: 'auto', background: 'rgba(255,255,255,0.02)', padding: 4, borderTop: '1px solid rgba(255,255,255,0.05)', color: '#FF7A1A' }}>
          [Vite] HMR Update: App.tsx reloaded in 4ms
        </div>
      </div>
    );
  }

  if (type === 'figma') {
    return (
      <div style={{ background: '#1E1E1E', height: '100%', width: '100%', display: 'flex', overflow: 'hidden', fontSize: 6 }}>
        <div style={{ width: '22%', borderRight: '1px solid #2C2C2C', padding: 4, display: 'flex', flexDirection: 'column', gap: 3, color: '#B3B3B3' }}>
          <div style={{ fontWeight: 600, color: '#FAFAFA' }}>Layers</div>
          <div># Frame 12</div>
          <div>&nbsp;&nbsp;Vector 1</div>
          <div>&nbsp;&nbsp;Text (Net Monitor)</div>
          <div>&nbsp;&nbsp;Rectangle 4</div>
        </div>
        <div style={{ flex: 1, background: '#2C2C2C', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '70%', height: '70%', border: '1px solid #FF7A1A', background: '#181818', borderRadius: 4, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: '#FAFAFA' }}>NET MONITOR</span>
            <span style={{ fontSize: 5, color: '#6B6B6B' }}>1440 x 900</span>
            <div style={{ position: 'absolute', top: -3, left: -3, width: 6, height: 6, background: '#FF7A1A' }} />
            <div style={{ position: 'absolute', bottom: -3, right: -3, width: 6, height: 6, background: '#FF7A1A' }} />
          </div>
        </div>
      </div>
    );
  }

  if (type === 'video') {
    return (
      <div style={{ background: '#0F0F11', height: '100%', width: '100%', padding: 4, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 3 }}>
        <div style={{ background: '#1D1E22', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'rgba(255,122,26,0.1)', color: '#FF7A1A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 6, fontWeight: 700 }}>SJ</div>
          <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 5, color: '#BDBDBD' }}>Sarah J.</span>
        </div>
        <div style={{ background: '#1D1E22', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'rgba(36,197,94,0.1)', color: '#22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 6, fontWeight: 700 }}>MC</div>
          <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 5, color: '#BDBDBD' }}>Michael C.</span>
        </div>
        <div style={{ background: '#1D1E22', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 6, fontWeight: 700 }}>DM</div>
          <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 5, color: '#BDBDBD' }}>David M.</span>
        </div>
        <div style={{ background: '#FF7A1A', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <span style={{ fontSize: 6, fontWeight: 700, color: '#111' }}>Screen Shared</span>
        </div>
      </div>
    );
  }

  // default browser
  return (
    <div style={{ background: '#F4F5F7', height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ background: '#E3E4E6', padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid #D1D2D5' }}>
        <div style={{ display: 'flex', gap: 3 }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#FF5F56' }} />
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#FFBD2E' }} />
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#27C93F' }} />
        </div>
        <div style={{ background: '#FFF', flex: 1, height: 10, borderRadius: 3, border: '1px solid #C4C5C8', display: 'flex', alignItems: 'center', padding: '0 4px', fontSize: 5, color: '#5A5A5A', overflow: 'hidden' }}>
          google.com/search?q=net-monitor-design
        </div>
      </div>
      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ height: 4, width: '45%', background: '#1A0DAB', borderRadius: 1 }} />
          <div style={{ height: 3, width: '90%', background: '#4A4A4A', borderRadius: 1 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ height: 4, width: '55%', background: '#1A0DAB', borderRadius: 1 }} />
          <div style={{ height: 3, width: '80%', background: '#4A4A4A', borderRadius: 1 }} />
        </div>
      </div>
    </div>
  );
}

// --- Custom dropdown filter for toolbar ---
function FilterSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'var(--input-bg)',
          border: '1px solid var(--border-light)',
          color: 'var(--text-primary)',
          padding: '8px 12px',
          borderRadius: 10,
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 130,
          cursor: 'pointer',
          justifyContent: 'space-between',
          transition: 'all 200ms'
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-primary)'}
        onMouseLeave={e => { if (!open) e.currentTarget.style.borderColor = 'var(--border-light)' }}
      >
        <span>{options.find(o => (typeof o === 'object' ? o.value : o) === value) ? (typeof options.find(o => (typeof o === 'object' ? o.value : o) === value) === 'object' ? options.find(o => (typeof o === 'object' ? o.value : o) === value).label : value) : value}</span>
        <ChevronDown size={14} color="var(--accent-primary)" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 6,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-light)',
          borderRadius: 10,
          padding: 4,
          zIndex: 60,
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          animation: 'slideUpFade 200ms both'
        }}>
          {options.map(opt => {
            const isObj = typeof opt === 'object';
            const optVal = isObj ? opt.value : opt;
            const optLabel = isObj ? opt.label : opt;
            return (
            <button
              key={optVal}
              onClick={() => { onChange(optVal); setOpen(false); }}
              style={{
                width: '100%',
                background: value === optVal ? 'rgba(var(--app-primary-color-rgb), 0.12)' : 'transparent',
                color: value === optVal ? 'var(--accent-primary)' : 'var(--text-secondary)',
                padding: '8px 10px',
                borderRadius: 6,
                fontSize: 12,
                textAlign: 'left',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.08)' }}
              onMouseLeave={e => { if (value !== optVal) e.currentTarget.style.background = 'transparent' }}
            >
              <span>{optLabel}</span>
              {value === optVal && <Check size={14} color="var(--accent-primary)" />}
            </button>
          )})}
        </div>
      )}
    </div>
  );
}


// --- Real WebRTC Video Component ---
function WebRTCVideo({ targetSocketId }) {
    const videoRef = useRef(null);
    const [stream, setStream] = useState(null);

    useEffect(() => {
        if (!targetSocketId) return;

        const unsubscribe = webrtcManager.subscribe(targetSocketId, (newStream, err) => {
            if (newStream) {
                setStream(newStream);
            } else {
                setStream(null);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [targetSocketId]);

    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
            videoRef.current.muted = true;
            videoRef.current.play().catch(err => console.warn("Video play error:", err));
        }
    }, [stream]);

    return (
        <div style={{ width: '100%', height: '100%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {stream ? (
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                    }}
                />
            ) : (
                <div style={{ color: '#FF7A1A', fontSize: 10 }}>Loading Stream...</div>
            )}
        </div>
    );
}

// --- Main View Component ---
export default function LiveMonitorView() {
  const [view, setView] = useState('Grid');
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All Departments');
  const [statusFilter, setStatusFilter] = useState('All');
  const [gridCols, setGridCols] = useState(3);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [refreshRate, setRefreshRate] = useState('5s');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageText, setMessageText] = useState('');

  const dispatch = useDispatch();
  const { messages: quickMessages } = useSelector((state) => state.quickMessage);

  useEffect(() => {
    dispatch(fetchMessages(Cookies.get('adminId')));
  }, [dispatch]);

  const [devices, setDevices] = useState([]);
  const [departments, setDepartments] = useState(['All Departments']);

  useEffect(() => {
    const adminId = Cookies.get('adminId') || localStorage.getItem('adminId');
    if (!adminId) return;
    fetch(`${API_BASE_URL}/departments?adminId=${adminId}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setDepartments(['All Departments', ...data.map(d => d.name || d.departmentName || d)]);
        }
      })
      .catch(err => console.error('Failed fetching departments:', err));
  }, []);

  useEffect(() => {
    localStorage.setItem('totalNodesEnrolled', devices.length);
  }, [devices.length]);

  useEffect(() => {
    const socket = adminSocketService.connect();
    webrtcManager.initialize();

    const handleDeviceList = (realDevices) => {
        setDevices(prev => {
            const mergedDevices = realDevices.map((rd, i) => {
                const existing = prev.find(p => p.socketId === rd.socketId);
                return {
                    id: rd.deviceId || rd.socketId,
                    deviceId: rd.deviceId,
                    socketId: rd.socketId,
                    empName: rd.assignedUser || rd.computerName || `User ${i+1}`,
                    dept: rd.department || 'Unknown',
                    pcName: rd.computerName || `PC-${i+1}`,
                    os: rd.os || 'Windows',
                    cpu: existing ? existing.cpu : (Math.floor(Math.random() * 30) + 10),
                    ram: existing ? existing.ram : (Math.floor(Math.random() * 40) + 20),
                    netSpd: existing ? existing.netSpd : '1.2 MB/s',
                    idleTime: rd.idleTime ? `${Math.floor(rd.idleTime / 60)}m ${Math.floor(rd.idleTime % 60)}s` : '0m',
                    lastActive: rd.isOnline === false ? 'Offline' : (rd.idleTime && rd.idleTime >= 20 ? 'Idle' : 'Active Now'),
                    resolution: '1920x1080',
                    status: rd.isOnline === false ? 'OFFLINE' : ((rd.idleTime && rd.idleTime >= 20) ? 'IDLE' : 'ONLINE'),
                    app: rd.activeApp || 'Desktop',
                    screenType: 'stream',
                    floor: 1,
                    room: 'Room A'
                };
            });
            
            return mergedDevices;
        });
    };

    socket.on('device-list', handleDeviceList);

    return () => {
        socket.off('device-list', handleDeviceList);
    };
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const rate = refreshRate === '1s' ? 1000 : refreshRate === '5s' ? 5000 : 10000;

    const interval = setInterval(() => {
      setDevices(prev => prev.map(d => {
        if (d.status === 'OFFLINE') return d;
        const newCpu = Math.max(2, Math.min(98, d.cpu + Math.floor((Math.random() - 0.5) * 12)));
        const newRam = Math.max(10, Math.min(95, d.ram + Math.floor((Math.random() - 0.5) * 4)));
        const spdNum = (Math.random() * 25).toFixed(1);

        return {
          ...d,
          cpu: newCpu,
          ram: newRam,
          netSpd: `${spdNum} MB/s`
        };
      }));
    }, rate);

    return () => clearInterval(interval);
  }, [refreshRate, autoRefresh]);

  const filteredDevices = devices.filter(d => {
    const matchesSearch = d.empName.toLowerCase().includes(search.toLowerCase()) ||
      d.pcName.toLowerCase().includes(search.toLowerCase()) ||
      d.app.toLowerCase().includes(search.toLowerCase());
    const matchesDept = deptFilter === 'All Departments' || d.dept === deptFilter;
    const matchesStatus = statusFilter === 'All' || d.status === statusFilter;
    return matchesSearch && matchesDept && matchesStatus;
  });

  const statuses = [
    { value: 'All', label: 'All' },
    { value: 'ONLINE', label: `ONLINE (${devices.filter(d => d.status === 'ONLINE').length})` },
    { value: 'OFFLINE', label: `OFFLINE (${devices.filter(d => d.status === 'OFFLINE').length})` },
    { value: 'IDLE', label: `IDLE (${devices.filter(d => d.status === 'IDLE').length})` }
  ];

  const getStatusProps = (status) => {
    switch (status) {
      case 'ONLINE': return { color: '#24D16F', label: 'ONLINE', pulse: true };
      case 'OFFLINE': return { color: '#FF5252', label: 'OFFLINE', pulse: false };
      case 'IDLE': return { color: '#FFC83D', label: 'IDLE', pulse: false };
      case 'LOCKED': return { color: '#3B82F6', label: 'LOCKED', pulse: false };
      case 'WARNING': return { color: '#FF7A1A', label: 'RISK / ALERT', pulse: true };
      default: return { color: '#9E9E9E', label: status, pulse: false };
    }
  };


  return (
    <div className="live-monitor-wrapper" style={selectedDevice ? { overflow: 'hidden', height: 'calc(100vh - 70px)', padding: 0 } : {}}>
      <ParticlesBg />

      {!selectedDevice && (
        <>
          <div className="live-monitor-header-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, zIndex: 10, position: 'relative' }}>
        <div>
          <h1 className="responsive-title" style={{ margin: 0, fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>Live Monitoring</h1>
          <p className="responsive-subtitle" style={{ color: 'var(--text-secondary)', margin: '4px 0 0', fontSize: 13 }}>
            <span style={{ color: '#24D16F', fontWeight: 700 }}>● {devices.filter(d => d.status !== 'OFFLINE').length} Devices Connected</span> • {devices.length} Total Nodes Enrolled
          </p>
        </div>
        <button
          className="mobile-filter-toggle"
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          title="Toggle Filters"
        >
          <Sliders size={18} />
        </button>
      </div>

      <div className={`dash-glass-card filter-container ${showMobileFilters ? 'show' : ''}`} style={{ padding: 18, marginBottom: 24, zIndex: 50, position: 'sticky', top: 0, background: 'var(--bg-card)', backdropFilter: 'blur(16px)', border: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', justifyContent: 'space-between' }}>

          <div className="filter-controls-group" style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div className="search-input-wrapper" style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Search Terminal</div>
              <div style={{ position: 'relative', width: '100%' }}>
                <Search size={14} color="var(--accent-primary)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Filter by name, app..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'var(--input-bg)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 10,
                    padding: '8px 12px 8px 34px',
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    outline: 'none',
                    transition: 'border 200ms'
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>
            </div>

            <FilterSelect label="Department" value={deptFilter} options={departments} onChange={setDeptFilter} />
            <FilterSelect label="Status" value={statusFilter} options={statuses} onChange={setStatusFilter} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>PCs Per Row</div>
              <div style={{ display: 'flex', background: 'var(--input-bg)', border: '1px solid var(--border-light)', borderRadius: 10, overflow: 'hidden' }}>
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <button
                    key={num}
                    onClick={() => setGridCols(num)}
                    style={{
                      background: gridCols === num ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'transparent',
                      color: gridCols === num ? 'var(--accent-primary)' : 'var(--text-muted)',
                      border: 'none',
                      borderRight: num !== 6 ? '1px solid var(--border-light)' : 'none',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontWeight: gridCols === num ? 700 : 400,
                      fontSize: 12,
                      transition: 'all 200ms',
                      flex: 1
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ zIndex: 10, position: 'relative' }}>
        {filteredDevices.length === 0 ? (
          <div className="dash-glass-card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)' }}>
            <Sliders size={32} color="var(--accent-primary)" style={{ marginBottom: 12, display: 'inline-block' }} />
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>No Devices Found</h3>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>Try adjusting your search criteria or filter options.</p>
          </div>
        ) : view === 'Grid' ? (
          <div className="grid-view-container" style={{ '--grid-cols': gridCols }}>
            {filteredDevices.map(d => {
              const sp = getStatusProps(d.status);
              return (
                <div
                  key={d.id}
                  className="dash-glass-card device-card"
                  style={{
                    padding: 0,
                    overflow: 'hidden',
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    cursor: 'pointer',
                    boxShadow: 'var(--card-shadow)',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-light)'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.borderColor = 'var(--accent-primary)';
                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(var(--app-primary-color-rgb), 0.25)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.borderColor = 'var(--border-light)';
                    e.currentTarget.style.boxShadow = 'var(--card-shadow)';
                  }}
                >
                  <div style={{ display: 'flex', justifySelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border-light)', background: 'rgba(var(--app-primary-color-rgb), 0.08)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: sp?.color, boxShadow: `0 0 8px ${sp?.color}` }} className={sp?.pulse ? 'anim-blink' : ''} />
                      <span style={{ fontSize: 10, fontWeight: 800, color: sp?.color, letterSpacing: '0.05em' }}>{sp?.label} {d.status === 'IDLE' ? `(${d.idleTime})` : ''}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDevice(d);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        padding: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'color 200ms'
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-primary)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                    >
                      <Maximize2 size={14} />
                    </button>
                  </div>

                  <div
                    onClick={() => setSelectedDevice(d)}
                    style={{ position: 'relative', aspectRatio: gridCols === 1 ? '21/9' : '16/9', borderBottom: '1px solid var(--border-light)', overflow: 'hidden' }}
                  >
                    {d.status === 'OFFLINE' ? (
                      <div style={{ width: '100%', height: '100%', background: '#121316', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#94A3B8' }}>
                        <Power size={24} color="#EF4444" />
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', color: '#94A3B8' }}>DEVICE POWERED DOWN</span>
                      </div>
                    ) : d.screenType === 'stream' || d.socketId ? (
                      <WebRTCVideo targetSocketId={d.socketId} />
                    ) : (
                      <MockScreen type={d.screenType} title={d.app} />
                    )}

                    {d.status !== 'OFFLINE' && (
                      <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, background: 'var(--bg-card)', backdropFilter: 'blur(8px)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.3)', padding: '4px 8px', borderRadius: 6, display: 'flex', gap: 6, alignItems: 'center', overflow: 'hidden' }}>
                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--accent-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.app}</span>
                      </div>
                    )}
                  </div>

                  <div style={{ padding: 14, background: 'rgba(var(--app-primary-color-rgb), 0.05)', borderTop: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(var(--app-primary-color-rgb), 0.18)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.3)', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>
                        {d.empName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{d.empName}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{d.dept} • {d.pcName}</div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMessageTarget({ id: d.deviceId, name: d.empName, dept: d.dept, pcName: d.pcName });
                        }}
                        style={{
                          marginLeft: 'auto',
                          background: 'rgba(var(--app-primary-color-rgb), 0.12)',
                          border: '1px solid rgba(var(--app-primary-color-rgb), 0.25)',
                          color: 'var(--accent-primary)',
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'var(--accent-primary)';
                          e.currentTarget.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.12)';
                          e.currentTarget.style.color = 'var(--accent-primary)';
                        }}
                      >
                        <MessageSquare size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : view === 'Department' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {departments.slice(1).map(dept => {
              const deptDevices = filteredDevices.filter(d => d.dept === dept);
              if (deptDevices.length === 0) return null;
              return (
                <div key={dept} className="dash-glass-card animate-scale-in" style={{ padding: 18, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
                  <h3 style={{ margin: '0 0 16px', color: 'var(--accent-primary)', fontSize: 16, fontWeight: 800, borderBottom: '1px solid var(--border-light)', paddingBottom: 8, letterSpacing: '0.05em' }}>{dept} ({deptDevices.length} Devices)</h3>
                  <div className="grid-view-container grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {deptDevices.map(d => (
                      <div key={d.id} className="dash-glass-card" style={{ padding: 12, background: 'rgba(var(--app-primary-color-rgb), 0.06)', border: '1px solid var(--border-light)', cursor: 'pointer', transition: 'all 0.2s ease' }} onClick={() => setSelectedDevice(d)} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-primary)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-light)'}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{d.empName}</span>
                          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: d.status === 'ONLINE' ? 'rgba(36,197,94,0.15)' : 'var(--input-bg)', color: d.status === 'ONLINE' ? '#22C55E' : 'var(--text-secondary)', fontWeight: 700 }}>{d.status} {d.status === 'IDLE' ? `(${d.idleTime})` : ''}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>PC: {d.pcName} • App: {d.app.split(' - ')[0]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : view === 'Floor' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {[1, 2, 3].map(floor => {
              const floorDevices = filteredDevices.filter(d => d.floor === floor);
              if (floorDevices.length === 0) return null;
              return (
                <div key={floor} className="dash-glass-card animate-scale-in" style={{ padding: 18, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
                  <h3 style={{ margin: '0 0 16px', color: 'var(--accent-primary)', fontSize: 16, fontWeight: 800, borderBottom: '1px solid var(--border-light)', paddingBottom: 8, letterSpacing: '0.05em' }}>Floor {floor} ({floorDevices.length} Devices)</h3>
                  <div className="grid-view-container grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {floorDevices.map(d => (
                      <div key={d.id} className="dash-glass-card" style={{ padding: 12, background: 'rgba(var(--app-primary-color-rgb), 0.06)', border: '1px solid var(--border-light)', cursor: 'pointer', transition: 'all 0.2s ease' }} onClick={() => setSelectedDevice(d)} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-primary)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-light)'}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{d.empName}</span>
                          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: d.status === 'ONLINE' ? 'rgba(36,197,94,0.15)' : 'var(--input-bg)', color: d.status === 'ONLINE' ? '#22C55E' : 'var(--text-secondary)', fontWeight: 700 }}>{d.status} {d.status === 'IDLE' ? `(${d.idleTime})` : ''}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>PC: {d.pcName} • App: {d.app.split(' - ')[0]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : view === 'Room' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {['Room A', 'Room B', 'Room C'].map(room => {
              const roomDevices = filteredDevices.filter(d => d.room === room);
              if (roomDevices.length === 0) return null;
              return (
                <div key={room} className="dash-glass-card animate-scale-in" style={{ padding: 18, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
                  <h3 style={{ margin: '0 0 16px', color: 'var(--accent-primary)', fontSize: 16, fontWeight: 800, borderBottom: '1px solid var(--border-light)', paddingBottom: 8, letterSpacing: '0.05em' }}>{room} ({roomDevices.length} Devices)</h3>
                  <div className="grid-view-container grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {roomDevices.map(d => (
                      <div key={d.id} className="dash-glass-card" style={{ padding: 12, background: 'rgba(var(--app-primary-color-rgb), 0.06)', border: '1px solid var(--border-light)', cursor: 'pointer', transition: 'all 0.2s ease' }} onClick={() => setSelectedDevice(d)} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-primary)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-light)'}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{d.empName}</span>
                          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: d.status === 'ONLINE' ? 'rgba(36,197,94,0.15)' : 'var(--input-bg)', color: d.status === 'ONLINE' ? '#22C55E' : 'var(--text-secondary)', fontWeight: 700 }}>{d.status} {d.status === 'IDLE' ? `(${d.idleTime})` : ''}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>PC: {d.pcName} • App: {d.app.split(' - ')[0]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="dash-glass-card animate-scale-in" style={{ padding: 0, overflow: 'hidden', background: 'var(--bg-card)' }}>
            <div className="dash-table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="dash-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Employee Name</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Dept</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>PC Details</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>CPU</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>RAM</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Net Speed</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Active App</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map(d => {
                    const sp = getStatusProps(d.status);
                    return (
                      <tr key={d.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '12px 16px', color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{d.empName}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 12 }}>{d.dept}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 12 }}>{d.pcName}</td>
                        <td style={{ padding: '12px 16px', color: d.cpu > 80 ? '#FF5252' : 'var(--text-primary)', fontSize: 12 }}>{d.cpu}%</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-primary)', fontSize: 12 }}>{d.ram}%</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-primary)', fontSize: 12 }}>{d.netSpd}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 12 }}>{d.app}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, background: d.status === 'ONLINE' ? 'rgba(36,197,94,0.1)' : 'var(--input-bg)', color: sp.color }}>
                            {sp.label} {d.status === 'IDLE' ? `(${d.idleTime})` : ''}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {messageTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="dash-glass-card animate-scale-in" style={{ width: '100%', maxWidth: 480, padding: 0, background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: 24, borderBottom: '1px solid var(--border-light)', background: 'rgba(var(--app-primary-color-rgb), 0.05)', backdropFilter: 'blur(10px)' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', border: '2px solid var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: 'var(--accent-primary)', background: 'rgba(var(--app-primary-color-rgb), 0.1)' }}>
                  {messageTarget.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: 6 }}>{messageTarget.name}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-primary)' }}></span>
                    {messageTarget.dept || 'Unknown'} • {messageTarget.pcName || messageTarget.name}
                  </div>
                </div>
              </div>
              <button 
                onClick={() => { setMessageTarget(null); setMessageText(''); }}
                style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', border: 'none', color: '#BDBDBD', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 200ms' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '24px 24px 0 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-muted)', letterSpacing: '0.05em' }}>QUICK MESSAGES</div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {quickMessages.map(msgObj => (
                  <button 
                    key={msgObj._id}
                    onClick={() => setMessageText(msgObj.text)}
                    style={{
                      background: messageText === msgObj.text ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'var(--input-bg)',
                      border: `1px solid ${messageText === msgObj.text ? 'var(--accent-primary)' : 'var(--border-light)'}`,
                      color: messageText === msgObj.text ? 'var(--accent-primary)' : 'var(--text-secondary)',
                      padding: '8px 16px',
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 200ms'
                    }}
                  >{msgObj.text}</button>
                ))}
              </div>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{ position: 'relative' }}>
                <textarea
                  autoFocus
                  rows={4}
                  placeholder={`Type a message to ${messageTarget.name}...`}
                  value={messageText}
                  onChange={e => setMessageText(e.target.value.substring(0, 300))}
                  style={{
                    width: '100%',
                    background: 'var(--input-bg)',
                    border: '1px solid var(--accent-primary)',
                    color: 'var(--text-primary)',
                    padding: 16,
                    borderRadius: 16,
                    resize: 'none',
                    outline: 'none',
                    fontSize: 15,
                    fontFamily: 'inherit',
                    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.1)'
                  }}
                />
                <div style={{ position: 'absolute', bottom: 16, right: 16, fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                  {messageText.length}/300
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button 
                  onClick={() => { setMessageTarget(null); setMessageText(''); }}
                  style={{ flex: 1, background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-light)', cursor: 'pointer', padding: '14px', borderRadius: 12, fontSize: 15, fontWeight: 600, transition: 'all 200ms' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--input-bg)'}
                >
                  Cancel
                </button>
                <button
                  disabled={!messageText.trim()}
                  onClick={() => {
                    const socket = adminSocketService.socket;
                    if (socket && messageText.trim()) {
                      socket.emit('admin-send-message', {
                        targetId: messageTarget.id,
                        message: messageText.trim()
                      });
                    }
                    setMessageTarget(null);
                    setMessageText('');
                  }}
                  style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: messageText.trim() ? 'var(--accent-primary)' : 'rgba(var(--app-primary-color-rgb), 0.3)', color: '#FFFFFF', border: 'none', cursor: messageText.trim() ? 'pointer' : 'default', padding: '14px', borderRadius: 12, fontSize: 15, fontWeight: 700, transition: 'all 200ms', boxShadow: messageText.trim() ? '0 4px 20px rgba(var(--app-primary-color-rgb), 0.35)' : 'none' }}
                >
                  <MessageSquare size={18} />
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RemoteViewer
        open={!!selectedDevice}
        onClose={() => setSelectedDevice(null)}
        targetSocketId={selectedDevice?.socketId}
        computerName={selectedDevice ? `${selectedDevice.empName} (${selectedDevice.pcName})` : ''}
        onSendMessage={() => {
          if (selectedDevice) {
            setMessageTarget({
              id: selectedDevice.deviceId || selectedDevice.id,
              name: selectedDevice.empName,
              dept: selectedDevice.dept,
              pcName: selectedDevice.pcName
            });
          }
        }}
        onPrev={() => {
          if (!selectedDevice) return;
          const idx = filteredDevices.findIndex(d => d.id === selectedDevice.id);
          if (idx > 0) setSelectedDevice(filteredDevices[idx - 1]);
          else setSelectedDevice(filteredDevices[filteredDevices.length - 1]);
        }}
        onNext={() => {
          if (!selectedDevice) return;
          const idx = filteredDevices.findIndex(d => d.id === selectedDevice.id);
          if (idx < filteredDevices.length - 1) setSelectedDevice(filteredDevices[idx + 1]);
          else setSelectedDevice(filteredDevices[0]);
        }}
      />

    </div>
  );
}
